import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useCompany } from './CompanyContext';
import { sanitizeForFirestore } from '../utils/firestoreUtils';
import { isAdminLike } from '../utils/roles';

const RoleContext = createContext();

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within RoleProvider');
  }
  return context;
};

export const RoleProvider = ({ children }) => {
  const { companyId, isCompanyLoading } = useCompany();
  // Role semantics:
  // - role === undefined  -> still hydrating (treat as loading)
  // - role === null       -> hydration complete but no role assigned
  const [role, setRole] = useState(undefined);
  const [loading, setLoading] = useState(true);

  // Load user role from Firestore using Firebase Auth listener
  useEffect(() => {
    console.log('🔍 [RoleContext] Initializing auth listener...');

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        console.log('❌ [RoleContext] No Firebase user detected.');
        // Hydration complete but no role in unauth state.
        setRole(null);
        setLoading(false);
        return;
      }

      // Wait for companyId (comes from /users/{uid}.companyId).
      if (isCompanyLoading || !companyId) {
        setRole(undefined);
        setLoading(true);
        return;
      }

      console.log('✅ [RoleContext] Firebase user detected:', user.uid, user.email);

      try {
        // Roles must be read from /companies/{companyId}/users/{uid}
        const membershipRef = doc(db, 'companies', companyId, 'users', user.uid);
        console.log('🔍 [RoleContext] Fetching membership doc:', `companies/${companyId}/users/${user.uid}`);

        setRole(undefined);
        setLoading(true);

        const membershipSnap = await getDoc(membershipRef);

        if (!membershipSnap.exists()) {
          // TODO: Decide if we should auto-create membership doc here.
          // For now: no guessing; keep user blocked until provisioning populates membership.
          console.warn('⚠️ [RoleContext] Membership doc missing. Role unavailable.');
          setRole(null);
          setLoading(false);
          return;
        }

        const membershipData = membershipSnap.data() || {};
        const loadedRole = membershipData.role ?? null;

        if (loadedRole === null) {
          console.warn('⚠️ [RoleContext] Membership role missing.');
          setRole(null);
        } else {
          setRole(loadedRole);
          console.log(
            `✅ [RoleContext] Membership doc exists at companies/${companyId}/users/${user.uid} with role: "${loadedRole}"`
          );
        }

        setLoading(false);
      } catch (error) {
        console.error('🔥 [RoleContext] Error loading role:', error);
        setRole(null);
        setLoading(false);
      }
    });

    return () => {
      console.log('🔍 [RoleContext] Cleaning up auth listener');
      unsubscribe();
    };
  }, [companyId, isCompanyLoading]);

  // Helper methods for role checks
  const isOwner = role === 'owner';
  const isAdmin = isAdminLike(role);
  const isSales = role === 'sales';
  const isTechnician = role === 'technician';
  const isMarketing = role === 'marketing';

  // Treat undefined as “still hydrating” (requested semantics)
  const isRoleHydrating = role === undefined;

  // Debug logging for role checks
  useEffect(() => {
    if (!loading && role) {
      console.log('🎭 [RoleContext] Role state updated:', role);
      console.log('🎭 [RoleContext] isOwner:', isOwner);
      console.log('🎭 [RoleContext] isAdmin:', isAdmin);
      console.log('🎭 [RoleContext] isSales:', isSales);
      console.log('🎭 [RoleContext] isTechnician:', isTechnician);
      console.log('🎭 [RoleContext] isMarketing:', isMarketing);
    }
  }, [role, loading, isOwner, isAdmin, isSales, isTechnician, isMarketing]);

  // Helper to update user role (admin use)
  // Roles are stored at /companies/{companyId}/users/{uid}
  const updateUserRole = async (userId, newRole) => {
    if (!companyId) return false;
    try {
      const membershipRef = doc(db, 'companies', companyId, 'users', userId);
      await setDoc(
        membershipRef,
        sanitizeForFirestore({
          role: newRole,
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );

      // NOTE: We are not mirroring profile fields here by design.

      return true;
    } catch (error) {
      console.error('Error updating user role:', error);
      return false;
    }
  };

  const value = {
    role,
    setRole,
    loading,
    isRoleHydrating,
    isOwner,
    isAdmin,
    isSales,
    isTechnician,
    isMarketing,
    updateUserRole,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};
